import { Component, OnInit, signal, ViewChild } from '@angular/core';
import { RouterModule } from '@angular/router';
import { TooltipModule } from 'primeng/tooltip';
import { samlPost } from '../../../../functions/saml-post';
import { ListItem } from '../../../../models/api/core/list/list-item';
import { ClaimRow } from '../../../../models/api/view/claim-view/claim-row';
import { BaseComponent } from '../../../base/base.component';
import { ButtonComponent } from '../../../core/buttons/button.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { ConfirmTabDialogComponent } from '../../../core/panels/confirm-tab-dialog.component';
import { TabPanelComponent } from '../../../core/panels/tab-panel/tab-panel.component';
import { TabComponent } from '../../../core/panels/tab-panel/tab.component';
import { ProviderSearchDialogComponent } from '../../../home/provider-search-dialog.component';
import { IcdComponent } from './icd.component';
import { TreatmentComponent } from './treatment.component';

@Component({
  selector: 'app-medical',
  imports: [
    RouterModule,
    PageHeaderComponent,
    ButtonComponent,
    TooltipModule,
    IcdComponent,
    TabPanelComponent,
    TabComponent,
    TreatmentComponent,
    ConfirmTabDialogComponent,
    ProviderSearchDialogComponent,
  ],
  template: `
    <app-confirm-tab-dialog
      #confirmDialog
      (confirm)="onProviderSearchClick()"
    />
    <app-provider-search-dialog
      #providerConfirmDialog
      [contracts]="isClientUser ? sbrContracts : []"
      (confirm)="onProviderSearchClick($event)"
    />
    <app-page-header label="Medical">
      <ng-template #right>
        @if (providerSearchEnabled()) {
          <app-button
            emphasis="Low"
            icon="fa-light fa-arrow-up-right-from-square"
            label="Provider search tool"
            (click)="openProviderSearch()"
          />
        }
      </ng-template>
    </app-page-header>
    <app-tab-panel>
      <app-tab label="ICD" [visible]="showIcdTab">
        <ng-template #content> <app-icd /></ng-template>
      </app-tab>
      <app-tab label="Treatment" [visible]="showTreatmentTab">
        <ng-template #content> <app-treatment /></ng-template>
      </app-tab>
    </app-tab-panel>
  `,
  styles: ``,
})
export class MedicalComponent extends BaseComponent implements OnInit {
  @ViewChild('confirmDialog')
  confirmDialog!: ConfirmTabDialogComponent;

  @ViewChild('providerConfirmDialog')
  providerConfirmDialog!: ProviderSearchDialogComponent;

  currentClaim: ClaimRow;
  showIcdTab = true;
  showTreatmentTab = true;

  providerSearchEnabled = signal<boolean>(false);
  sbrContracts: ListItem[] = [];
  isClientUser = false;

  ngOnInit(): void {
    this.currentClaim = this.view.currentClaim();
    this.isClientUser = this.session.user.isClient ?? false;

    // ICD tab: requires OPTIONS-DETAIL token (user-level)
    this.showIcdTab = this.session.authorize('Details');

    // Treatment tab: requires 'ManagedCare' token; hidden for DS/GL
    this.showTreatmentTab =
      this.view.authorize('ManagedCare') &&
      !['DS', 'GL'].includes(this.currentClaim?.lineOfBusiness);

    this.api.home.resourceHub().then((resourceHub) => {
      this.providerSearchEnabled.set(resourceHub.providerSearchEnabled);
      this.sbrContracts = resourceHub.sbrContracts ?? [];
    });

    this.view.hideLoading();
  }

  openProviderSearch(): void {
    if (this.isClientUser) {
      this.providerConfirmDialog.show();
    } else {
      this.confirmDialog.show();
    }
  }

  async onProviderSearchClick(selectedContract?: string | null): Promise<void> {
    const response = await this.api.medical.getProviderSearchSaml(
      selectedContract ?? null,
    );

    if (response.success) {
      samlPost(response.samlFields, response.directorUrl, 'SAMLFields');
    }
  }
}
